import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import axios from "axios";
import type { ApiResponse } from "../types";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

/**
 * Learner bootstrap — single call that returns every catchtivity-service read the dashboard
 * needs: the active corporate assignment list, unified learner performance, and (when the nested
 * `contestParams` / `supportAssignmentParams` are supplied) the weekly contest list + active support-assignment
 * list. Replaces the three parallel catchtivity calls LearnerApp used to fire on dashboard mount.
 */
export const retrieveLearnerCatchtivityBootstrap = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.bootstrap.context}${endpoints.catchtivities.bootstrap.learner.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * One deneme sınavı's full result for the calling learner — every lesson and kazanım row plus the
 * Tahmini puan — for the /reports/mock-exams/:mockExamId karne. The backend pins identity to the token
 * and answers 404 until the deneme is published for the learner's şube.
 */
export const retrieveLearnerMockExamReport = async (queryParams: {
  userId: number;
  userProfileId: number;
  mockExamId: number;
}): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["mock-exams"].context}${endpoints.catchtivities["mock-exams"].learner.report.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Practise bootstrap — the /practise screen's lazy "previous content" fetch as a single call:
 * previous (ended) corporate assignments (count-only) + previous support assignments. Replaces the two
 * parallel requests PractiseNowScreen used to fire on first mount.
 */
export const retrieveLearnerPractiseBootstrap = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.bootstrap.context}${endpoints.catchtivities.bootstrap.learner.practise.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Solve bootstrap — the solver screens' mount fan-out (source by id + activity progress list +
 * activity answer list, plus the diagnostic state for corporate assignments) as a single call.
 * sourceType is one of BRANCH_ASSIGNMENT / PERSONALIZED_ASSIGNMENT / STANDARD_EXAM
 * (sourceId = the exam application id) / CONTEST; exactly the matching source DTO field comes back
 * populated. Callers keep the standalone fetchers as a fallback for when this endpoint errors
 * (e.g. a frontend deployed ahead of the backend).
 */
export const retrieveLearnerSolveBootstrap = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.bootstrap.context}${endpoints.catchtivities.bootstrap.learner.solve.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Corporate Assignment
 */
export const queryBranchAssignmentListBySeasonIdAndGradeIdAndBranchIdAndCurriculumType =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["branch-assignments"].context}${endpoints.catchtivities["branch-assignments"].query.dto["by-seasonId-gradeId-branchId-curriculumType"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

/**
 * Previous (expired + evaluated) corporate assignments for the learner — the /practise
 * "Önceki ödevlerim" surface. Fetched lazily when /practise mounts (never at dashboard
 * time), so the initial load no longer carries every homework since season start. Returns
 * count-only DTOs (no activity content, no completion status).
 */
export const queryPreviousBranchAssignmentListBySeasonIdAndGradeIdAndBranchIdAndCurriculumType =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["branch-assignments"].context}${endpoints.catchtivities["branch-assignments"].query.dto.previous["by-seasonId-gradeId-branchId-curriculumType"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const retrieveBranchAssignmentById = async (id): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities["branch-assignments"].context
      }${endpoints.catchtivities["branch-assignments"].dto.id.context.replace(
        "{id}",
        id,
      )}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveBranchAssignmentProgressByIdAndUserIdAndUserProfileId =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["branch-assignments"].context}${endpoints.catchtivities["branch-assignments"]["with-progresses"]["by-id-userId-userProfileId"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const retrieveBranchAssignmentPerformance = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["branch-assignments"].context}${endpoints.catchtivities["branch-assignments"].performance.learner.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Activity
 */
export const retrieveActivityWithDataById = async (id): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities.activities.context
      }${endpoints.catchtivities.activities["with-data"].dto.id.context.replace(
        "{id}",
        id,
      )}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Batch counterpart of retrieveActivityWithDataById — all requested activities' content, fetched
 * in chunks and merged into one flat list. Response order is NOT guaranteed to match idList; map
 * items by id. Used by the fan-out spots of the solve loops (complete-with-defaults, support-assignment
 * set solver).
 *
 * The response uses the heavy `with-data` shape (full activity content + templates), so the id
 * list is split into bounded chunks (a large support-assignment set / assignment can pass every activity
 * id). The chunks run through a small worker pool — at most CHUNK_CONCURRENCY heavy POSTs in flight
 * at once — capping both a single request/response AND the aggregate concurrent burst. Each chunk
 * is settled independently: a single failed/transient chunk drops only its own (≤ BATCH_SIZE)
 * activities, the rest still load (callers map by id, so partial results and cross-chunk order are
 * both fine). Only a total failure (every chunk errored) surfaces as `{ err }`; an empty id list
 * short-circuits to an empty list (no request).
 */
const ACTIVITY_WITH_DATA_BATCH_SIZE = 50;
const ACTIVITY_WITH_DATA_CHUNK_CONCURRENCY = 3;
export const queryActivityWithDataDTOListByIdList = async (idList): Promise<ApiResponse> => {
  if (!idList?.length) return { data: [] };
  const url = `${connection.base.catchtivity.url}${endpoints.catchtivities.activities.context}${endpoints.catchtivities.activities.query.dto["with-data"]["by-idList"].context}`;
  const chunks: any[][] = [];
  for (let i = 0; i < idList.length; i += ACTIVITY_WITH_DATA_BATCH_SIZE) {
    chunks.push(idList.slice(i, i + ACTIVITY_WITH_DATA_BATCH_SIZE));
  }
  const settled: Array<{ ok: boolean; data: any[] }> = new Array(chunks.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < chunks.length) {
      const index = cursor++;
      try {
        const response = await axios.post(url, { idList: chunks[index] });
        settled[index] = {
          ok: true,
          data: Array.isArray(response.data) ? response.data : [],
        };
      } catch (err) {
        settled[index] = { ok: false, data: [] };
      }
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(ACTIVITY_WITH_DATA_CHUNK_CONCURRENCY, chunks.length) },
      () => worker(),
    ),
  );
  if (chunks.length > 0 && settled.every((chunkResult) => !chunkResult.ok)) {
    return { err: new Error("all activity-with-data chunks failed") };
  }
  const merged = settled.flatMap((chunkResult) => chunkResult.data);
  return { data: merged, status: 200 };
};

export const retrieveActivityWithDataByIdAndToken = async (id, token): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities.activities.context
      }${endpoints.catchtivities.activities["with-data"].dto.id.context.replace(
        "{id}",
        id,
      )}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Activity Answer
 */
export const createActivityAnswerBySourceTypeAndSourceIdAndActivityIdAndActivityTemplateId =
  async (createParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-answers"].context}${endpoints.catchtivities["activity-answers"]["by-sourceType-sourceId-activityId-activityTemplateId"].context}`,
        createParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const patchActivityAnswerWithData = async (patchParams): Promise<ApiResponse> => {
  try {
    const response = await axios.patch(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-answers"].context}${endpoints.catchtivities["activity-answers"]["with-data"].context}`,
      patchParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const saveActivityProgressAndAnswer = async (params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-answers"].context}${endpoints.catchtivities["activity-answers"]["save-with-progress"].context}`,
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryActivityAnswerListBySourceTypeAndSourceId = async (
  queryParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-answers"].context}${endpoints.catchtivities["activity-answers"].query.dto["by-sourceType-sourceId"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryActivityAnswerListBySourceTypeAndSourceIdAndActivityId =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-answers"].context}${endpoints.catchtivities["activity-answers"].query.dto["by-sourceType-sourceId-activityId"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

/**
 * Activity Progress
 */
export const createActivityProgressBySourceTypeAndSourceIdAndActivityIdAndActivityTemplateId =
  async (createParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-progresses"].context}${endpoints.catchtivities["activity-progresses"]["by-sourceType-sourceId-activityId-activityTemplateId"].context}`,
        createParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const patchActivityProgress = async (patchParams): Promise<ApiResponse> => {
  try {
    const response = await axios.patch(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-progresses"].context}/`,
      patchParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryActivityProgressDTOListBySourceTypeAndSourceId = async (
  queryParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-progresses"].context}/query/dto/by-sourceType-sourceId`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Activity Reviews
 */
export const createActivityReviewBySourceTypeAndSourceIdAndActivityIdAndActivityTemplateId =
  async (createParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-reviews"].context}${endpoints.catchtivities["activity-reviews"]["by-sourceType-sourceId-activityId-activityTemplateId"].context}`,
        createParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const queryActivityReviewListByUserIdAndUserProfileId = async (
  queryParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-reviews"].context}${endpoints.catchtivities["activity-reviews"].query.dto["by-userId-userProfileId"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const patchActivityReview = async (patchParams): Promise<ApiResponse> => {
  try {
    const response = await axios.patch(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-reviews"].context}/`,
      patchParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Corporate Assignment (with data)
 */
export const retrieveBranchAssignmentWithActivityDataById = async (id): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities["branch-assignments"].context
      }${endpoints.catchtivities["branch-assignments"].dto[
        "with-data"
      ].id.context.replace("{id}", id)}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveBranchAssignmentWithActivityDataByIdAndToken = async (
  id,
  token,
): Promise<ApiResponse> => {
  const axiosInstance = axios.create();
  try {
    const response = await axiosInstance.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities["branch-assignments"].context
      }${endpoints.catchtivities["branch-assignments"].dto[
        "with-data"
      ].id.context.replace("{id}", id)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Corporate Standard Exam
 */
export const retrieveStandardExamWithActivityDataById = async (id): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities["standard-exams"].context
      }${endpoints.catchtivities["standard-exams"].dto[
        "with-data"
      ].id.context.replace("{id}", id)}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveStandardExamWithActivityDataByIdAndToken = async (
  id,
  token,
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities["standard-exams"].context
      }${endpoints.catchtivities["standard-exams"].dto[
        "with-data"
      ].id.context.replace("{id}", id)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Corproate Standard Exam Application
 */
export const queryStandardExamApplicationListBySeasonIdAndGradeIdAndBranchIdAndCurriculumType =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["standard-exam-applications"].context}${endpoints.catchtivities["standard-exam-applications"].query.dto["by-seasonId-gradeId-branchId-curriculumType"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const retrieveStandardExamApplicationById = async (id): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${
        endpoints.catchtivities["standard-exam-applications"].context
      }${endpoints.catchtivities[
        "standard-exam-applications"
      ].dto.id.context.replace("{id}", id)}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveStandardExamApplicationProgressByIdAndUserIdAndUserProfileId =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["standard-exam-applications"].context}${endpoints.catchtivities["standard-exam-applications"].dto["with-progresses"]["by-id-userId-userProfileId"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

/**
 * Personalized Assignment
 */
export const createPersonalizedAssignmentFromBaseActivityList = async (
  createParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignments"].context}${endpoints.catchtivities["personalized-assignments"]["from-base-activity-list"].context}`,
      createParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const createOrResumePersonalizedAssignmentFromBaseActivityList = async (
  createParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignments"].context}${endpoints.catchtivities["personalized-assignments"]["resume-or-create-from-base-activity-list"].context}`,
      createParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryPersonalizedAssignmentDTOListByUserIdAndUserProfileId =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignments"].context}${endpoints.catchtivities["personalized-assignments"].query.dto["by-userId-userProfileId"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const retrievePersonalizedAssignmentById = async (id): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignments"].context}${endpoints.catchtivities["personalized-assignments"].dto.id.context}`.replace(
        "{id}",
        id,
      ),
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Learning-path readiness — batch lock summary for the path's WEAK outcome cards. Body carries
 * only { categoryIdList }; identity is pinned server-side from the JWT. Per target: prerequisite
 * counts + locked (any weak/never-assessed direct prerequisite).
 */
export const getReadinessSummary = async (readinessParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignments"].context}${endpoints.catchtivities["personalized-assignments"].readiness.summary.context}`,
      readinessParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Learning-path readiness — one level of the drill-down view: the target's display block plus its
 * direct prerequisites as full readiness items (each with its OWN second-level lock counts). Body
 * carries only { targetCategoryId }; identity is pinned server-side from the JWT.
 */
export const getReadinessDetail = async (readinessParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignments"].context}${endpoints.catchtivities["personalized-assignments"].readiness.detail.context}`,
      readinessParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const evaluatePersonalizedAssignment = async (evaluateParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignments"].context}${endpoints.catchtivities["personalized-assignments"].evaluate.context}`,
      evaluateParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Activity Template Feedback
 */
export const createActivityTemplateFeedbackByToken = async (
  activityTemplateFeedback,
  token,
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-template-feedbacks"].context}${endpoints.catchtivities["activity-template-feedbacks"]["by-token"].context}`,
      activityTemplateFeedback,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryActivityTemplateFeedbackListByToken = async (
  queryParams,
  token,
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-template-feedbacks"].context}${endpoints.catchtivities["activity-template-feedbacks"].query.dto["by-activityId"].context}`,
      queryParams,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Contest
 */
export const retrieveContestById = async (contestId): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.contests.context}${endpoints.catchtivities.contests.dto.id.context}`.replace(
        "{id}",
        contestId,
      ),
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const createContestByActivityGroupId = async (createParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.contests.context}${endpoints.catchtivities.contests["by-activityGroupId"].context}`,
      createParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryContestListByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.contests.context}${endpoints.catchtivities.contests.query.dto["by-params"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveContestProgress = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.contests.context}${endpoints.catchtivities.contests.progress.dto["by-contestId"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const evaluateContestByParams = async (evaluateParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.contests.context}${endpoints.catchtivities.contests.evaluate.context}`,
      evaluateParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Activity Evaluations
 */
export const queryWrongActivityEvaluationListByParams = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-evaluations"].context}${endpoints.catchtivities["activity-evaluations"].check["wrong-activities"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Activity Template Chat
 */
export const initializeActivityTemplateChatWithText = async (startParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-template-chats"].context}${endpoints.catchtivities["activity-template-chats"].initialize.context}`,
      startParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const addActivityTemplateChatWithText = async (
  activityTemplateChatId,
  addParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["activity-template-chats"].context}${endpoints.catchtivities["activity-template-chats"]["activity-template-chat-id"].items.context}`.replace(
        "{activityTemplateChatId}",
        activityTemplateChatId,
      ),
      addParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Photo solutions — the learner's own "Fotoğrafla çöz" history (read-only). Written server-side
 * after a successful photo solve; the owner is pinned to the token, so no ids are sent.
 */
export const queryMyPhotoSolutionList = async (queryParams: {
  page: number;
  size: number;
}): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["photo-solutions"].context}${endpoints.catchtivities["photo-solutions"].mine.context}`,
      { params: queryParams },
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrievePhotoSolutionById = async (id): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["photo-solutions"].context}${endpoints.catchtivities["photo-solutions"].id.context.replace(
        "{id}",
        id,
      )}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Corporate Assignment Feedback
 */
export const generateBranchAssignmentFeedback = async (generateParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["branch-assignment-feedbacks"].context}${endpoints.catchtivities["branch-assignment-feedbacks"].generate.context}`,
      generateParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const generatePersonalizedAssignmentFeedback = async (generateParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities["personalized-assignment-feedbacks"].context}${endpoints.catchtivities["personalized-assignment-feedbacks"].generate.context}`,
      generateParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Learning Path Progress
 */
export const retrieveDuelLobbyBootstrap = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.bootstrap.lobby.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const createDuel = async (createParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.create.context}`,
      createParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const joinDuel = async (duelId, joinParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.join.context}`.replace(
        "{duelId}",
        duelId,
      ),
      joinParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const joinDuelByCode = async (joinParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["join-by-code"].context}`,
      joinParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryPublicDuels = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.query.public.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveActiveDuel = async (userProfileId): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.active.context}?userProfileId=${userProfileId}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveDuelById = async (duelId): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.dto.id.context}`.replace(
        "{id}",
        duelId,
      ),
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const submitDuelAnswer = async (duelId, roundNumber, answerData): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.rounds.answer.context}`
        .replace("{duelId}", duelId)
        .replace("{roundNumber}", roundNumber),
      answerData,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const getLeaveLobbyUrl = (duelId) => {
  return `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["leave-lobby"].context}`.replace(
    "{duelId}",
    duelId,
  );
};

export const getAbandonDuelUrl = (duelId) => {
  return `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.abandon.context}`.replace(
    "{duelId}",
    duelId,
  );
};

export const abandonDuel = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.abandon.context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const setDuelReady = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.ready.context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const kickDuelPlayer = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.kick.context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const leaveDuelLobby = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["leave-lobby"].context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const setDuelUnready = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.unready.context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryOngoingDuels = async (queryParams): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.query.ongoing.context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const spectateDuelByCode = async (params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["spectate-by-code"].context}`,
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const joinDuelAsSpectator = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.spectate.context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const leaveDuelSpectator = async (duelId, userProfileId): Promise<ApiResponse> => {
  try {
    const response = await axios.delete(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.spectate.context}`.replace(
        "{duelId}",
        duelId,
      ) + `?userProfileId=${userProfileId}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveDuelRecord = async (userId, userProfileId): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.record.context}?userId=${userId}&userProfileId=${userProfileId}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

// The caller's finished (COMPLETED / ABANDONED) duels, newest first — the backend caps the list.
export const retrieveDuelHistory = async (userId, userProfileId): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.history.context}?userId=${userId}&userProfileId=${userProfileId}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const sendDuelReaction = async (duelId, params): Promise<void> => {
  try {
    await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.reaction.context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
  } catch (err) {
    // Silently ignore reaction errors
  }
};

// Peers with live presence, each annotated with `online` (boolean) and `status` (EPresenceStatus). Needs no
// params: identity and scope come from the token.
//
// ⚠️ WHAT THIS RETURNS DEPENDS ON THE DEPLOYED BACKEND, so do not treat it as a friends-only feed:
//   - catchtivity BEFORE catchupper-parent#621: the caller's whole SEASON ROSTER intersected with the
//     institution's presence set. No friendship filter — and `validateInviteEligibility` has no friendship
//     check either, so a non-friend is invitable.
//   - catchtivity FROM #621 on: the caller's ACCEPTED FRIENDS only, offline ones included with
//     `online: false` / `status: "OFFLINE"` so they can be listed dimmed.
//
// Because the narrowed contract is not guaranteed, the panel does NOT rely on it: FriendsOnlinePanel
// intersects these rows with management's accepted-friend list before rendering, so a non-friend is never
// shown and their presence never leaks even against a pre-#621 backend. Keep that intersection. The name
// "online-peers" is from the pre-#621 contract and is why this is easy to get wrong.
export const retrieveOnlinePeers = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["online-peers"].context}`,
      {},
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

// Invite a peer into an existing duel the caller is in — params.kind is "OPPONENT" (take the open
// player-2 slot of a waiting lobby) or "SPECTATOR" (watch).
export const inviteToDuel = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels.invite.context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

// Host removes a spectator from their duel.
export const kickDuelSpectator = async (duelId, params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["kick-spectator"].context}`.replace(
        "{duelId}",
        duelId,
      ),
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const acceptDuelInvite = async (inviteId): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["invite-accept"].context}`.replace(
        "{inviteId}",
        inviteId,
      ),
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const declineDuelInvite = async (inviteId): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["invite-decline"].context}`.replace(
        "{inviteId}",
        inviteId,
      ),
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const cancelDuelInvite = async (inviteId): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["invite-cancel"].context}`.replace(
        "{inviteId}",
        inviteId,
      ),
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

// Recover any still-valid duel invites addressed to the caller (caller-pinned server-side) — used to
// re-seed the invite queue when a live websocket push was missed (reconnect / just-mounted gap).
export const retrievePendingDuelInvites = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.catchtivity.url}${endpoints.catchtivities.duels.context}${endpoints.catchtivities.duels["invites-pending"].context}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveAudioTranscriptsBySubjectDescriptionId = async (
  subjectDescriptionId,
): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.storage.url}/storage-files/file-translations/by-subject-description-id/${subjectDescriptionId}`,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};
